import {
  Container,
  LinearProgress,
  Paper,
  Snackbar,
  Typography,
} from "@material-ui/core";
import PropTypes from "prop-types";
import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { useAlertMessage } from "../../shared/hooks/useAlertMessage";
import { useHistory } from "../../shared/hooks/useHistory";
import { WizardIcon } from "../../shared/icons";
import { Footer } from "../footer";
import { FooterControls } from "../footerControls/footer-controls.component";
import { Header } from "../header";
import { getData } from "../storage";
import { WizardContext, initialWizardState } from "./context";
import { setBackControlVisible, setStep } from "./reducer/actions";
import { WizardReducer } from "./reducer/reducer";
import TermsAndPolicies from "./termsAndPolicies";
import { wizardStyles } from "./wizard.styles";

const OnboardingTitle =
  "Welcome to Mentoro! Let's personalize your experience.";
const OnboardingSubtitle = "What do you want to achieve?";

export const Wizard = ({ renderSteps, steps }) => {
  const history = useHistory();
  const indexStep = useRef(0);
  const handleNextEvent = useRef(null);
  const handleSubmitEvent = useRef(null);
  const [title, setTitle] = useState(OnboardingTitle);
  const [subtitle, setSubtitle] = useState(OnboardingSubtitle);
  const [, setPercent] = useState(0);
  const { request } = useSelector((state) => state.request);
  const { isOpen, openAlertMesssage, handleClose } = useAlertMessage();
  const [wizardState, dispatchWizard] = useReducer(
    WizardReducer,
    initialWizardState
  );
  const classes = wizardStyles({ step: wizardState.step });
  const getIndexStep = () => {
    return steps.findIndex((step) => step.id === wizardState.step);
  };

  useEffect(() => {
    let currentStep = steps.findIndex((step) => step.id === getData("STEP"));
    currentStep = currentStep === -1 ? 0 : currentStep;
    indexStep.current = currentStep;
    dispatchWizard(setStep(steps[currentStep].id));
    history.replace({ search: { step: steps[currentStep].id } });
  }, []);

  useEffect(() => {
    let hideBackControl = true;
    const currentIndexStep = getIndexStep();
    indexStep.current = currentIndexStep;

    if (currentIndexStep > 0) hideBackControl = false;
    if (wizardState.step === "CREATE_USER") {
      setSubtitle("");
    }
    dispatchWizard(setBackControlVisible(hideBackControl));
  }, [wizardState.step]);

  const onHandleNextEvent = (hanldeNextEvent) => {
    handleNextEvent.current = hanldeNextEvent;
  };

  const onHandleSubmit = (handleSubmit) => {
    handleSubmitEvent.current = handleSubmit;
  };

  useEffect(() => {
    if (request.isOpen && !request.isFetching) openAlertMesssage();
  }, [request]);

  const onSubmit = useCallback(() => {
    let currentIndexStep = getIndexStep();

    if (currentIndexStep === steps.length - 1) {
      handleSubmitEvent.current?.();

      return;
    }

    handleNextEvent.current?.();

    if (currentIndexStep === -1) {
      currentIndexStep = 0;
    } else {
      currentIndexStep += 1;
    }

    dispatchWizard(setStep(steps[currentIndexStep].id));
    history.replace({ search: { step: steps[currentIndexStep].id } });
  }, [wizardState.step, handleNextEvent.current]);

  const onBack = useCallback(() => {
    const currentIndexStep = getIndexStep();

    if (currentIndexStep > 0) {
      dispatchWizard(setStep(steps[currentIndexStep - 1].id));
      history.replace({ search: { step: steps[currentIndexStep - 1].id } });
    }
  }, [wizardState.step]);

  const setNextControlLabel = useMemo(() => {
    const isLastStep = getIndexStep() === steps.length - 1;
    return isLastStep ? "Create Profile" : undefined;
  }, [wizardState.step]);

  if (request.isFetching)
    return (
      <>
        <Header />
        <LinearProgress color="secondary" />
      </>
    );

  return (
    <WizardContext.Provider
      value={{
        state: wizardState,
        dispatch: dispatchWizard,
        onHandleNextEvent,
        onHandleSubmit,
        setTitle,
        setSubtitle,
        setPercent,
      }}
    >
      <Paper className={classes.paper}>
        <Header />
        <div className={classes.picContainer}>
          <WizardIcon />
        </div>
        <Container>
          <div className={classes.titleContainer}>
            <div className={classes.innerContainer}>
              <Typography variant="body1" className={classes.title}>
                {title || steps[indexStep.current]?.title}
              </Typography>
              <Typography variant="body1" className={classes.subtitle}>
                {subtitle || steps[indexStep.current]?.subtitle}
              </Typography>
            </div>
          </div>

          <div className={classes.container}>
            {renderSteps(wizardState.step)}
          </div>
          <FooterControls
            nextControlText={setNextControlLabel}
            hideBackControl={wizardState.hideBackControl}
            isNextButtonDisabled={wizardState.disableNextControl}
            onSubmit={onSubmit}
            onBack={onBack}
          />
          {wizardState.step === "CREATE_USER" && <TermsAndPolicies />}
          <Footer />
        </Container>
      </Paper>
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        open={isOpen}
        autoHideDuration={6000}
        onClose={handleClose}
        message={request.message}
      />
    </WizardContext.Provider>
  );
};

Wizard.propTypes = {
  renderSteps: PropTypes.func.isRequired,
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      title: PropTypes.string,
    })
  ).isRequired,
};
